import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { agenciesService, AdminAgencyRecord, AgencyFilters } from '@/services/agencies';
import { plansService, AdminPlan } from '@/services/plans';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { TableSkeleton } from '@/components/skeletons';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { BILLING_STATUS_CONFIG, CHURN_TYPES_FOR, CHURN_TYPE_CONFIG, LIFECYCLE_STAGE_CONFIG, TRIAL_RISK_CONFIG } from '@/lib/billingStatus';
import { manualAccessTooltip } from '@/lib/manualAccess';
import {
  Search, RefreshCw, Pencil, AlertCircle, Building2, Filter, Trash2, AlertTriangle, Loader2, Hash, X, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { generatePaginationPages } from '@/lib/pagination';
import { toDateParam, fromDateParam } from '@/lib/date-param';
import { useIsViewer, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { RelativeTime } from '@/components/RelativeTime';
import { useDebounce } from '@/hooks/useDebounce';
import { EditAgencyModal } from './EditAgencyModal';
import type { DateRange } from 'react-day-picker';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import toastService from '@/services/toast/service';

function getStatusBadge(billingStatus: string, manualAccessReason?: string | null) {
  const config = BILLING_STATUS_CONFIG[billingStatus] || BILLING_STATUS_CONFIG.free;
  return (
    <Tooltip>
      <TooltipTrigger className="cursor-default">
        <Badge tone={config.tone}>
          {config.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {billingStatus === 'manual' && (
          <p className="font-semibold">{manualAccessTooltip(manualAccessReason)}</p>
        )}
        <p>{config.tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function getStageBadge(lifecycleStage: string) {
  const config = LIFECYCLE_STAGE_CONFIG[lifecycleStage];
  if (!config) return <span className="text-muted-foreground text-sm">Unknown</span>;
  return (
    <Tooltip>
      <TooltipTrigger className="cursor-default">
        <Badge tone={config.tone}>
          {config.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <p>{config.tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}

const DAY_MS = 1000 * 60 * 60 * 24;

/** "Trial ends in 3d" / "Trial ended", the same day count the agency detail shows. */
function trialEndsLabel(endsAtUtc: string | null | undefined): string | null {
  if (!endsAtUtc) return null;
  const daysLeft = Math.ceil((new Date(endsAtUtc).getTime() - Date.now()) / DAY_MS);
  return daysLeft > 0 ? `Trial ends in ${daysLeft}d` : 'Trial ended';
}

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** Badge for a scored trial. Agencies that aren't trialing carry no risk and get an empty cell. */
function getTrialRiskCell(agency: AdminAgencyRecord) {
  const risk = agency.trialRisk;
  const config = risk ? TRIAL_RISK_CONFIG[risk.level] : undefined;
  if (!risk || !config) return null;
  const endsAt = agency.stripeTrialEndsAtUtc ? new Date(agency.stripeTrialEndsAtUtc) : null;
  const endsLabel = trialEndsLabel(agency.stripeTrialEndsAtUtc);
  return (
    <div className="flex flex-col items-center gap-0.5">
      <Tooltip>
        <TooltipTrigger className="cursor-default">
          <Badge tone={config.tone}>
            {config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{config.tooltip}</p>
          <p>{plural(risk.completedSubmissions, 'completed submission')}, {plural(risk.appointments, 'booking')}</p>
          {endsAt && <p>Trial ends {endsAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>}
        </TooltipContent>
      </Tooltip>
      {endsLabel && <span className="text-xs text-muted-foreground">{endsLabel}</span>}
    </div>
  );
}

const COLUMN_COUNT = 12;

const digitsOnly = (value: string) => value.replace(/\D/g, '');

// Agencies owned by these super admins must never be deleted from the backoffice
const PROTECTED_SUPER_ADMIN_EMAILS = new Set(['ceo@passive-automation.com', 'joe@doe.com']);

/** Why Delete is disabled once "select all matching" is on. */
const SELECT_ALL_EXPORT_ONLY =
  'Select all is for export only. Tick rows individually to delete.';

const isProtectedAgency = (agency: AdminAgencyRecord) =>
  !!agency.superAdminEmail && PROTECTED_SUPER_ADMIN_EMAILS.has(agency.superAdminEmail.toLowerCase());

export default function AgenciesList() {
  const navigate = useNavigate();
  // The search, filters and page live in the URL so leaving for an agency and
  // coming back (or reloading, or sharing the link) lands on the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialParams = useRef(searchParams).current;

  const [agencies, setAgencies] = useState<AdminAgencyRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(() => initialParams.get('search') || '');
  const [page, setPage] = useState(() => Math.max(1, parseInt(initialParams.get('page') || '1', 10) || 1));
  const [totalPages, setTotalPages] = useState(1);
  const [totalAgencies, setTotalAgencies] = useState<number | null>(null);

  // Filter state
  const [filters, setFilters] = useState<AgencyFilters>(() => ({
    status: initialParams.get('status') || undefined,
    stage: initialParams.get('stage') || undefined,
    churnType: initialParams.get('churnType') || undefined,
    planId: initialParams.get('planId') || undefined,
    trialRisk: initialParams.get('trialRisk') || undefined,
    dateFrom: initialParams.get('dateFrom') || undefined,
    dateTo: initialParams.get('dateTo') || undefined,
    minSubmissions: digitsOnly(initialParams.get('minSubmissions') || '') || undefined,
    maxSubmissions: digitsOnly(initialParams.get('maxSubmissions') || '') || undefined,
  }));
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const from = fromDateParam(initialParams.get('dateFrom'));
    const to = fromDateParam(initialParams.get('dateTo'));
    return from || to ? { from, to } : undefined;
  });
  const [minSubmissions, setMinSubmissions] = useState(() => digitsOnly(initialParams.get('minSubmissions') || ''));
  const [maxSubmissions, setMaxSubmissions] = useState(() => digitsOnly(initialParams.get('maxSubmissions') || ''));
  const [submissionsOpen, setSubmissionsOpen] = useState(false);
  const [plans, setPlans] = useState<AdminPlan[]>([]);

  const debouncedSearch = useDebounce(searchQuery, 600);
  const debouncedMinSubmissions = useDebounce(minSubmissions, 400);
  const debouncedMaxSubmissions = useDebounce(maxSubmissions, 400);
  const [editingAgency, setEditingAgency] = useState<AdminAgencyRecord | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [agencyToDelete, setAgencyToDelete] = useState<AdminAgencyRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmationChecked, setDeleteConfirmationChecked] = useState(false);

  // Cross-page selection, same hook and semantics as the dashboard's data
  // tables. 'all' mode means "every agency matching the filters", tracked as a
  // count minus exclusions so it never has to enumerate unloaded ids.
  const selection = useBulkSelection({
    totalCount: totalAgencies ?? 0,
    resetKey: JSON.stringify([debouncedSearch, filters]),
  });
  const [isExporting, setIsExporting] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const isViewer = useIsViewer();
  const [bulkDeleteConfirmationChecked, setBulkDeleteConfirmationChecked] = useState(false);

  // Which churn refinements the current status/stage admits. Empty means the
  // selection isn't about churn, so the Churn dropdown stays hidden.
  const churnOptions = CHURN_TYPES_FOR(filters.status, filters.stage);

  // Check if any filters are active
  const hasActiveFilters = !!(
    filters.status || filters.stage || filters.churnType || filters.planId || filters.trialRisk || dateRange?.from ||
    filters.minSubmissions || filters.maxSubmissions
  );

  const submissionsLabel =
    minSubmissions && maxSubmissions ? `${minSubmissions} to ${maxSubmissions}`
      : minSubmissions ? `${minSubmissions}+`
        : maxSubmissions ? `up to ${maxSubmissions}`
          : '';

  // A churn refinement outlives its parent selection if we don't clear it, and
  // it would then narrow the list with no visible control to explain why.
  useEffect(() => {
    if (filters.churnType && !churnOptions.includes(filters.churnType)) {
      setFilters(prev => ({ ...prev, churnType: undefined }));
    }
  }, [filters.churnType, churnOptions]);

  // Only trials are scored, so the risk dropdown is shown with the Trialing
  // status and its selection is dropped along with it.
  const showTrialRisk = filters.status === 'trialing';
  useEffect(() => {
    if (filters.trialRisk && !showTrialRisk) {
      setFilters(prev => ({ ...prev, trialRisk: undefined }));
    }
  }, [filters.trialRisk, showTrialRisk]);

  // Load plans for the filter dropdown
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const data = await plansService.getPlans();
        setPlans(data);
      } catch {
        // Silently fail — filters will just not show plan options
      }
    };
    loadPlans();
  }, []);

  // Sync dateRange to filters. Bails out when nothing actually changed so the
  // state restored from the URL doesn't count as a filter change on mount.
  useEffect(() => {
    const dateFrom = dateRange?.from ? toDateParam(dateRange.from) : undefined;
    const dateTo = dateRange?.to ? toDateParam(dateRange.to) : undefined;
    setFilters(prev =>
      prev.dateFrom === dateFrom && prev.dateTo === dateTo ? prev : { ...prev, dateFrom, dateTo }
    );
  }, [dateRange]);

  // Same for the completed submissions bounds, once the typing settles.
  useEffect(() => {
    const min = debouncedMinSubmissions || undefined;
    const max = debouncedMaxSubmissions || undefined;
    setFilters(prev =>
      prev.minSubmissions === min && prev.maxSubmissions === max
        ? prev
        : { ...prev, minSubmissions: min, maxSubmissions: max }
    );
  }, [debouncedMinSubmissions, debouncedMaxSubmissions]);

  // The request in flight. A new fetch aborts it, so a slow earlier response
  // (a superseded search, a filter change) can never overwrite newer rows.
  const inFlight = useRef<AbortController | null>(null);

  const fetchAgencies = useCallback(async () => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    try {
      setIsLoading(true);
      setError(null);
      // Newest first, unless the list is narrowed to trials: then riskiest
      // first, soonest trial end breaking ties.
      const res = await agenciesService.getAgencies(
        page, 20, debouncedSearch, showTrialRisk ? 'trialRisk' : 'createdAtUtc', 'desc', filters,
        controller.signal,
      );
      setAgencies(res.data);
      setTotalPages(res.meta.totalPages);
      setTotalAgencies(res.meta.total);
    } catch (err: any) {
      if (controller.signal.aborted) return;
      setError(err.response?.data?.message || 'Failed to load agencies. Please try again later.');
    } finally {
      // An aborted request leaves the spinner to the one that replaced it.
      if (inFlight.current === controller) setIsLoading(false);
    }
  }, [page, debouncedSearch, filters, showTrialRisk]);

  useEffect(() => () => inFlight.current?.abort(), []);

  // A search or filter change goes back to page 1 before it fetches, so it
  // costs one request rather than one for the old page and one for the first.
  // The key the URL restored on mount is not a change, or the restored page
  // would be thrown away.
  const filterKey = JSON.stringify([debouncedSearch, filters]);
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current !== filterKey) {
      lastFilterKey.current = filterKey;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    fetchAgencies();
  }, [fetchAgencies, filterKey, page]);

  // Mirror the applied search, filters and page back into the URL. Writing only
  // on a real change keeps the replace() out of a feedback loop with the router.
  const lastQuery = useRef(initialParams.toString());
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedSearch) next.set('search', debouncedSearch);
    if (filters.status) next.set('status', filters.status);
    if (filters.stage) next.set('stage', filters.stage);
    if (filters.churnType) next.set('churnType', filters.churnType);
    if (filters.planId) next.set('planId', filters.planId);
    if (filters.trialRisk) next.set('trialRisk', filters.trialRisk);
    if (filters.dateFrom) next.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) next.set('dateTo', filters.dateTo);
    if (filters.minSubmissions) next.set('minSubmissions', filters.minSubmissions);
    if (filters.maxSubmissions) next.set('maxSubmissions', filters.maxSubmissions);
    if (page > 1) next.set('page', String(page));

    const nextQuery = next.toString();
    if (nextQuery === lastQuery.current) return;
    lastQuery.current = nextQuery;
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, filters, page, setSearchParams]);

  const handleDeleteClick = (agency: AdminAgencyRecord) => {
    setAgencyToDelete(agency);
    setDeleteModalOpen(true);
    setDeleteConfirmationChecked(false);
  };

  const confirmDelete = async () => {
    if (!agencyToDelete) return;
    setIsDeleting(true);
    try {
      await agenciesService.deleteAgency(agencyToDelete.id);
      toastService.success(`"${agencyToDelete.name}" deleted`);
      fetchAgencies();
    } catch (err: any) {
      toastService.error(err.response?.data?.message || "Couldn't delete agency");
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setAgencyToDelete(null);
    }
  };

  const handleBulkDeleteClick = () => {
    setBulkDeleteConfirmationChecked(false);
    setBulkDeleteModalOpen(true);
  };

  const confirmBulkDelete = async () => {
    // Only ever the explicitly ticked rows. "Select all matching" is an
    // export-only mode, so it must never reach the delete loop.
    const ids = selection.selectedIds;
    if (ids.length === 0 || selection.mode === 'all') return;
    setIsBulkDeleting(true);
    let succeeded = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await agenciesService.deleteAgency(id);
        succeeded++;
      } catch {
        failed++;
      }
    }
    if (succeeded > 0) toastService.success(`${succeeded} agenc${succeeded === 1 ? 'y' : 'ies'} deleted`);
    if (failed > 0) toastService.error(`Couldn't delete ${failed} agenc${failed === 1 ? 'y' : 'ies'}`);
    setIsBulkDeleting(false);
    setBulkDeleteModalOpen(false);
    selection.clear();
    fetchAgencies();
  };

  // Exports the ticked rows across every page, not just this one. The button
  // only renders with a selection, so the empty-ids case cannot be reached from
  // here; the service still accepts it.
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { rows, truncated, rowLimit } = await agenciesService.exportAgencies(
        debouncedSearch,
        filters,
        selection.mode === 'all'
          ? { excludeIds: selection.excludedIds }
          : { ids: selection.selectedIds },
      );
      if (truncated) {
        toastService.warning(`Exported the first ${rowLimit.toLocaleString()} agencies`);
      } else {
        toastService.success(
          `Exported ${rows.toLocaleString()} agenc${rows === 1 ? 'y' : 'ies'}`,
        );
      }
    } catch {
      toastService.error("Couldn't export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const handleUpdateSuccess = () => {
    fetchAgencies();
  };

  return (
    <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
          Agencies{' '}
          {totalAgencies !== null && (
            <span className="text-muted-foreground font-normal">
              ({totalAgencies})
            </span>
          )}
        </h1>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {selection.count > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="w-full sm:w-auto">
                  <Button
                    variant="destructive"
                    onClick={handleBulkDeleteClick}
                    disabled={
                      isLoading || isBulkDeleting || isViewer || selection.mode === 'all'
                    }
                    className="w-full sm:w-auto"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete ({selection.selectedIds.length})
                  </Button>
                </span>
              </TooltipTrigger>
              {isViewer ? (
                <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>
              ) : selection.mode === 'all' ? (
                <TooltipContent>{SELECT_ALL_EXPORT_ONLY}</TooltipContent>
              ) : null}
            </Tooltip>
          )}
          {/* Export follows the ticks, same as Delete: with nothing selected
              there is nothing to export. Everything is still one click away
              through the header checkbox. */}
          {selection.count > 0 && (
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isLoading || isExporting}
              className="w-full sm:w-auto"
            >
              <Download className={`h-4 w-4 mr-2 ${isExporting ? 'animate-pulse' : ''}`} />
              {isExporting ? 'Exporting…' : `Export CSV (${selection.count.toLocaleString()})`}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => fetchAgencies()}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : (
        <div className="rounded-lg border bg-card">
          {/* Toolbar — search left, filters right */}
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border-b">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search agencies or super admins..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9${isLoading ? ' opacity-50 cursor-not-allowed' : ''}`}
                readOnly={isLoading}
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Select
                value={filters.status || 'all'}
                onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-9 w-[130px] text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="paying">Active</SelectItem>
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="cancelling">Cancelling</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="trial_expired">Trial Expired</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="incomplete">Incomplete</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="not_selected">Not selected</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.stage || 'all'}
                onValueChange={(v) => setFilters(prev => ({ ...prev, stage: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-9 w-[150px] text-sm">
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  <SelectItem value="signed_up:wizard">Signup: Wizard</SelectItem>
                  <SelectItem value="signed_up:plans">Signup: Plans</SelectItem>
                  <SelectItem value="signed_up:checkout">Signup: Checkout</SelectItem>
                  <SelectItem value="onboarded">Onboarded</SelectItem>
                  <SelectItem value="activated">Activated</SelectItem>
                  <SelectItem value="value_realized">Value Realized</SelectItem>
                  <SelectItem value="power_user">Power User</SelectItem>
                  <SelectItem value="churning">Churning</SelectItem>
                  <SelectItem value="churned">Churned</SelectItem>
                </SelectContent>
              </Select>

              {churnOptions.length > 0 && (
                <Select
                  value={filters.churnType || 'all'}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, churnType: v === 'all' ? undefined : v }))}
                >
                  <SelectTrigger className="h-9 w-[190px] text-sm">
                    <SelectValue placeholder="Churn" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All churn types</SelectItem>
                    {churnOptions.map((type) => (
                      <SelectItem key={type} value={type}>
                        {CHURN_TYPE_CONFIG[type].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {showTrialRisk && (
                <Select
                  value={filters.trialRisk || 'all'}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, trialRisk: v === 'all' ? undefined : v }))}
                >
                  <SelectTrigger className="h-9 w-[180px] text-sm">
                    <SelectValue placeholder="Trial risk" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Object.entries(TRIAL_RISK_CONFIG).map(([level, config]) => (
                      <SelectItem key={level} value={level}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select
                value={filters.planId || 'all'}
                onValueChange={(v) => setFilters(prev => ({ ...prev, planId: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-9 w-[150px] text-sm">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All plans</SelectItem>
                  <SelectItem value="none">Not selected</SelectItem>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Popover open={submissionsOpen} onOpenChange={setSubmissionsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-9 w-[260px] justify-start text-left text-sm font-normal ${submissionsLabel ? '' : 'text-muted-foreground'}`}
                  >
                    <Hash className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {submissionsLabel ? `Submissions ${submissionsLabel}` : 'Submissions'}
                    </span>
                    {submissionsLabel && (
                      <div
                        role="button"
                        tabIndex={0}
                        aria-label="Clear submissions filter"
                        className="ml-auto flex shrink-0 items-center justify-center rounded-sm opacity-50 hover:opacity-100 p-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setMinSubmissions('');
                          setMaxSubmissions('');
                          setSubmissionsOpen(false);
                        }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </div>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" align="end">
                  <p className="text-sm font-medium mb-2">Completed submissions</p>
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Min"
                      value={minSubmissions}
                      onChange={(e) => setMinSubmissions(digitsOnly(e.target.value))}
                      className="h-9 w-[78px] text-sm"
                      aria-label="Minimum completed submissions"
                    />
                    <span className="text-sm text-muted-foreground">to</span>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Max"
                      value={maxSubmissions}
                      onChange={(e) => setMaxSubmissions(digitsOnly(e.target.value))}
                      className="h-9 w-[78px] text-sm"
                      aria-label="Maximum completed submissions"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Leave either side blank for no bound.
                  </p>
                </PopoverContent>
              </Popover>

              <DateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                className="w-[230px] text-sm"
                disabled={isLoading}
              />
            </div>
          </div>
          
          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[40px] text-center">
                    <Checkbox
                      checked={selection.headerState}
                      onCheckedChange={() => selection.toggleAll()}
                      disabled={!totalAgencies}
                      aria-label="Select all agencies matching the current filters"
                    />
                  </TableHead>
                  <TableHead className="min-w-[200px]">Name</TableHead>
                  <TableHead className="text-center w-[180px]">Super Admin</TableHead>
                  <TableHead className="text-center w-[110px]">Subaccounts</TableHead>
                  <TableHead className="text-center w-[80px]">Forms</TableHead>
                  <TableHead className="text-center w-[120px]">Completed Submissions</TableHead>
                  <TableHead className="text-center w-[100px]">Plan</TableHead>
                  <TableHead className="text-center w-[120px]">Stripe status</TableHead>
                  <TableHead className="text-center w-[140px]">Stage</TableHead>
                  <TableHead className="text-center w-[170px]">Trial risk</TableHead>
                  <TableHead className="text-center w-[120px]">Created At</TableHead>
                  <TableHead className="text-center w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className={ isLoading ? 'pointer-events-none' : '' }>
              {isLoading ? (
                <TableSkeleton columns={COLUMN_COUNT} rows={5} />
              ) : agencies.length === 0 && debouncedSearch ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Search className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">No results found</h3>
                      <p className="text-sm text-muted-foreground">
                        No agencies match your search. Try a different query.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : agencies.length === 0 && hasActiveFilters ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Filter className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">No matching agencies</h3>
                      <p className="text-sm text-muted-foreground">
                        No agencies match your current filters.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : agencies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Building2 className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">No agencies found</h3>
                      <p className="text-sm text-muted-foreground">
                        There are no agencies in the system yet.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                agencies.map((agency) => (
                  <TableRow
                    key={agency.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate({ pathname: `/agencies/${agency.id}`, search: searchParams.toString() })}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()} className="text-center">
                      <Checkbox
                        checked={selection.isSelected(agency.id)}
                        onCheckedChange={() => selection.toggleRow(agency.id)}
                        disabled={isProtectedAgency(agency)}
                        aria-label={`Select ${agency.name}`}
                      />
                    </TableCell>
                    <TableCell>
                      {/* A real link, so right-click, cmd-click and middle-click open the
                          agency in a new tab. The row's own click still opens it here. */}
                      <Link
                        to={{ pathname: `/agencies/${agency.id}`, search: searchParams.toString() }}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium hover:underline"
                      >
                        {agency.name}
                      </Link>
                    </TableCell>

                    <TableCell className="text-center">
                      {agency.superAdminName ? (
                        <div className="flex flex-col items-center justify-center">
                          <span className="font-medium text-sm">{agency.superAdminName}</span>
                          <span className="text-xs text-muted-foreground">{agency.superAdminEmail}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm italic">No Admin</span>
                      )}
                    </TableCell>

                    <TableCell className="text-center font-medium text-sm">
                      {agency.subaccountCount}
                    </TableCell>

                    <TableCell className="text-center font-medium text-sm">
                      {agency.formsCount ?? 0}
                    </TableCell>

                    <TableCell className="text-center">
                      {(agency.completedSubmissionsCount ?? agency.submissionsCount ?? 0).toLocaleString()}
                    </TableCell>

                    <TableCell className="text-center">
                      {agency.planName ? (
                        <span className="font-medium text-sm">{agency.planName}</span>
                      ) : (
                        <span className="text-muted-foreground text-sm">Not selected</span>
                      )}
                    </TableCell>

                    <TableCell className="text-center">
                      {getStatusBadge(agency.billingStatus, agency.manualAccessReason)}
                    </TableCell>

                    <TableCell className="text-center">
                      {getStageBadge(agency.lifecycleStage)}
                    </TableCell>

                    <TableCell className="text-center">
                      {getTrialRiskCell(agency)}
                    </TableCell>

                    <TableCell className="text-center text-muted-foreground">
                      <RelativeTime date={agency.createdAtUtc} className="cursor-default" />
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center">
                        <div className="inline-flex items-center rounded-md border">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={isProtectedAgency(agency) || isViewer}
                                  className="h-8 w-8 rounded-none rounded-l-md border-r hover:bg-destructive/10 hover:text-destructive"
                                  onClick={() => handleDeleteClick(agency)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : isProtectedAgency(agency) ? 'This agency cannot be deleted' : 'Delete Agency'}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={isViewer}
                                  className="h-8 w-8 rounded-none rounded-r-md"
                                  onClick={() => setEditingAgency(agency)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : 'Edit Stripe Records'}</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
              </TableBody>
            </Table>
          </div>
          
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {agencies.length} of {totalAgencies !== null ? totalAgencies : '...'} agencies
              </p>
              <Pagination className={`mx-0 w-auto ${isLoading ? 'pointer-events-none opacity-50' : ''}`}>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  {generatePaginationPages(page, totalPages).map((item, i) =>
                    item === 'ellipsis' ? (
                      <PaginationItem key={`ellipsis-${i}`}>
                        <span className="px-2 text-muted-foreground">…</span>
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={item}>
                        <PaginationLink
                          onClick={() => setPage(item as number)}
                          isActive={(item as number) === page}
                          className="cursor-pointer"
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      )}

      {editingAgency && (
        <EditAgencyModal
          agency={editingAgency}
          open={!!editingAgency}
          onOpenChange={(open: boolean) => !open && setEditingAgency(null)}
          onSuccess={handleUpdateSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <AlertDialogTitle>Delete Agency</AlertDialogTitle>
                <AlertDialogDescription className="mt-1">
                  This action cannot be undone.
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogBody>
            <div>
              <p className="text-sm text-muted-foreground mb-4 break-words">
                Are you sure you want to delete <strong className="break-all">{agencyToDelete?.name}</strong>? All associated forms, leads, subaccounts, and data will be permanently deleted.
              </p>
              <div className="flex items-center space-x-2 rounded-lg border bg-muted/50 p-3">
                <Checkbox
                  id="confirm-delete-agency"
                  checked={deleteConfirmationChecked}
                  onCheckedChange={(checked) => setDeleteConfirmationChecked(checked as boolean)}
                />
                <Label htmlFor="confirm-delete-agency" className="text-sm font-medium cursor-pointer">
                  I understand that this action is permanent
                </Label>
              </div>
            </div>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={!deleteConfirmationChecked || isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Modal */}
      <AlertDialog open={bulkDeleteModalOpen} onOpenChange={setBulkDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <AlertDialogTitle>Delete {selection.selectedIds.length} agenc{selection.selectedIds.length === 1 ? 'y' : 'ies'}</AlertDialogTitle>
                <AlertDialogDescription className="mt-1">
                  This action cannot be undone.
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogBody>
            <div>
              <p className="text-sm text-muted-foreground mb-4 break-words">
                Are you sure you want to delete {selection.selectedIds.length} selected agenc{selection.selectedIds.length === 1 ? 'y' : 'ies'}? All associated forms, leads, subaccounts, and data will be permanently deleted.
              </p>
              <div className="flex items-center space-x-2 rounded-lg border bg-muted/50 p-3">
                <Checkbox
                  id="confirm-bulk-delete-agency"
                  checked={bulkDeleteConfirmationChecked}
                  onCheckedChange={(checked) => setBulkDeleteConfirmationChecked(checked as boolean)}
                />
                <Label htmlFor="confirm-bulk-delete-agency" className="text-sm font-medium cursor-pointer">
                  I understand that this action is permanent
                </Label>
              </div>
            </div>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              disabled={!bulkDeleteConfirmationChecked || isBulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {isBulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isBulkDeleting ? 'Deleting...' : `Delete ${selection.selectedIds.length} agenc${selection.selectedIds.length === 1 ? 'y' : 'ies'}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
